import React from 'react';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';

export const AcceptableUsePolicyPage: React.FC = () => {
  return (
    <DocsLayout
      title='Acceptable Use Policy'
      description='Guidelines for responsible use of the Vehix platform'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Legal', path: '/docs/legal' },
        { label: 'Acceptable Use' }
      ]}
    >
      <div className='max-w-[1000px]'>
        <p className='text-sm text-muted-foreground italic mb-6'>Last Updated: January 28, 2026</p>

        <div className='mb-6'>
          <Card className='border-blue-500/50 bg-blue-500/5'>
            <CardHeader>
              <CardTitle className='text-lg text-blue-700 dark:text-blue-400'>Purpose</CardTitle>
            </CardHeader>
            <CardContent className='text-sm leading-6'>
              <p>
                This Acceptable Use Policy outlines prohibited uses of the Vehix platform. By using
                Vehix, you agree to comply with this policy and use the service responsibly and
                lawfully.
              </p>
            </CardContent>
          </Card>
        </div>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>1. General Principles</h2>
          <p className='text-sm leading-6 mb-4'>You agree to use Vehix:</p>
          <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
            <li>In compliance with all applicable laws and regulations</li>
            <li>In a manner that does not harm the service or other users</li>
            <li>Solely for its intended business purpose</li>
            <li>With respect for the rights and privacy of others</li>
          </ul>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>2. Prohibited Activities</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>2.1 Security Violations</h3>
            <p className='text-sm leading-6 mb-4'>You may not:</p>
            <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
              <li>Attempt to gain unauthorized access to any part of the service</li>
              <li>Probe, scan, or test the vulnerability of the system</li>
              <li>Breach or circumvent any security or authentication measures</li>
              <li>
                Access data not intended for you or log into an account you are not authorized to
                access
              </li>
              <li>Interfere with service to any user, host, or network</li>
              <li>Introduce viruses, malware, or other malicious code</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>2.2 Abuse and Misuse</h3>
            <p className='text-sm leading-6 mb-4'>You may not:</p>
            <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
              <li>
                Use the service to store or transmit infringing, libelous, or unlawful material
              </li>
              <li>
                Use the service to store or transmit material in violation of third-party rights
              </li>
              <li>Send unsolicited communications or spam</li>
              <li>Harass, threaten, or defraud other users</li>
              <li>Impersonate any person or entity</li>
              <li>Misrepresent your affiliation with any person or entity</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>2.3 System Abuse</h3>
            <p className='text-sm leading-6 mb-4'>You may not:</p>
            <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
              <li>Overburden or interfere with the service or networks connected to it</li>
              <li>
                Use automated systems to access the service in a manner that sends more requests
                than a human could reasonably produce
              </li>
              <li>Frame or mirror any part of the service</li>
              <li>Reverse engineer, decompile, or disassemble the platform</li>
              <li>Modify, adapt, or create derivative works of the platform</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>2.4 Data Misuse</h3>
            <p className='text-sm leading-6 mb-4'>You may not:</p>
            <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
              <li>Access or use data of other organizations without authorization</li>
              <li>Export or share confidential customer data inappropriately</li>
              <li>Use the service to collect personal information about other users</li>
              <li>Share your account credentials with unauthorized individuals</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>2.5 Do Not Copy or Share</h3>
            <p className='text-sm leading-6 mb-4'>
              Vehix is owned by Kebi AI LLC and is protected by law. You may not:
            </p>
            <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
              <li>Take screenshots or recordings of Vehix to share outside your organization</li>
              <li>Copy any text, images, or content from Vehix for use elsewhere</li>
              <li>Share information about how Vehix works with anyone outside your organization</li>
              <li>
                Discuss Vehix features, pricing, or functionality with competitors or outside
                parties
              </li>
              <li>Post about Vehix internal features on social media or public forums</li>
              <li>Use any part of Vehix to create a similar product or service</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>2.6 Keep It Confidential</h3>
            <p className='text-sm leading-6 mb-4'>
              The following information is confidential and must not be shared:
            </p>
            <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
              <li>How Vehix features work and what they do</li>
              <li>The design and layout of the platform</li>
              <li>Any pricing or business information</li>
              <li>Documentation, training materials, and help guides</li>
              <li>Information about upcoming features or changes</li>
              <li>Any communication from Kebi AI about the product</li>
            </ul>
            <p className='text-sm leading-6 mt-4'>
              You may only share this information with authorized users at your organization who
              need it for their work.
            </p>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>3. Responsible Use Guidelines</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>3.1 Data Accuracy</h3>
            <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
              <li>Provide accurate and complete information</li>
              <li>Keep your account information up to date</li>
              <li>Do not enter false or misleading data</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>3.2 Account Security</h3>
            <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
              <li>Maintain the confidentiality of your login credentials</li>
              <li>Use strong authentication methods</li>
              <li>Report suspected security incidents immediately</li>
              <li>Log out when finished using the platform</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>3.3 Resource Usage</h3>
            <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
              <li>Use the service efficiently and avoid wasteful practices</li>
              <li>Do not attempt to circumvent usage limits or quotas</li>
              <li>Report technical issues rather than attempting workarounds</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>3.4 Review Before You Act</h3>
            <p className='text-sm leading-6 mb-4'>
              Vehix provides suggestions to help you work more efficiently. You should:
            </p>
            <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
              <li>Always review route and technician suggestions before confirming them</li>
              <li>Make sure assignments make sense for your specific situation</li>
              <li>Use your professional judgment - do not rely solely on system suggestions</li>
              <li>Report any suggestions that seem incorrect or unusual</li>
            </ul>
            <p className='text-sm leading-6 mt-4'>
              You are responsible for all decisions you make, even when following system
              suggestions.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>3.5 Use the System Fairly</h3>
            <p className='text-sm leading-6 mb-4'>To get the best results from Vehix:</p>
            <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
              <li>Enter accurate information about recalls, technicians, and dealers</li>
              <li>Do not try to trick the system with false data to get different suggestions</li>
              <li>Keep technician skills and availability information up to date</li>
              <li>Report any issues honestly rather than working around them</li>
            </ul>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>4. Reporting Violations</h2>

          <div className='mb-6'>
            <Card className='border-red-500/50 bg-red-500/5'>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <AlertCircle className='h-5 w-5 text-red-600' />
                  <CardTitle className='text-lg text-red-700 dark:text-red-400'>
                    Report Policy Violations
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className='text-sm leading-6'>
                <p className='mb-3'>
                  If you become aware of any violation of this Acceptable Use Policy, please report
                  it immediately:
                </p>
                <div>
                  <strong>Security Incidents:</strong>
                  <div className='text-primary'>security@kebi.ai</div>
                </div>
                <div className='mt-2'>
                  <strong>Other Violations:</strong>
                  <div className='text-primary'>abuse@kebi.ai</div>
                </div>
                <div className='mt-3 text-muted-foreground'>
                  All reports will be investigated promptly and handled confidentially.
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>5. Consequences of Violations</h2>
          <p className='text-sm leading-6 mb-6'>
            Violations of this Acceptable Use Policy may result in:
          </p>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>5.1 Warning</h3>
            <p className='text-sm leading-6'>
              For minor or first-time violations, we may issue a written warning and require
              corrective action.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>5.2 Temporary Suspension</h3>
            <p className='text-sm leading-6'>
              For repeated or more serious violations, we may temporarily suspend your account while
              we investigate.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>5.3 Permanent Termination</h3>
            <p className='text-sm leading-6 mb-4'>
              For severe or ongoing violations, we may permanently terminate your account,
              including:
            </p>
            <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
              <li>Security breaches or hacking attempts</li>
              <li>Illegal activity</li>
              <li>Repeated policy violations after warnings</li>
              <li>Fraud or misrepresentation</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>5.4 Legal Action</h3>
            <p className='text-sm leading-6'>
              We reserve the right to take legal action for violations that cause harm to our
              business, other users, or third parties.
            </p>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>6. Monitoring and Enforcement</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>6.1 Right to Monitor</h3>
            <p className='text-sm leading-6'>
              We reserve the right to monitor use of the service to ensure compliance with this
              policy, though we do not routinely monitor content.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>6.2 Investigation</h3>
            <p className='text-sm leading-6'>
              We may investigate suspected violations and cooperate with law enforcement when
              required.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>6.3 Preservation</h3>
            <p className='text-sm leading-6'>
              We may preserve and disclose account information and content if required by law or
              necessary to protect rights, property, or safety.
            </p>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>7. Examples of Acceptable Use</h2>
          <p className='text-sm leading-6 mb-4'>To clarify, acceptable uses of Vehix include:</p>
          <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
            <li>Managing vehicle recall campaigns for your dealership</li>
            <li>Assigning recalls to authorized technicians</li>
            <li>Tracking route progress and completion</li>
            <li>Generating reports for your organization</li>
            <li>Communicating with customers about their vehicle recalls</li>
            <li>Configuring campaigns and dealer settings</li>
            <li>Training employees on the platform</li>
          </ul>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>8. Updates to This Policy</h2>
          <p className='text-sm leading-6 mb-4'>
            We may update this Acceptable Use Policy from time to time. Continued use of Vehix after
            changes constitutes acceptance of the updated policy. We will notify you of material
            changes by:
          </p>
          <ul className='text-sm leading-6 space-y-2 list-disc pl-6'>
            <li>Posting the updated policy on the platform</li>
            <li>Updating the date at the top of this page</li>
            <li>Sending email notification for significant changes</li>
          </ul>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>9. Questions</h2>
          <p className='text-sm leading-6 mb-6'>
            If you have questions about this Acceptable Use Policy or what constitutes acceptable
            use, contact us at:
          </p>

          <div className='mb-6 p-6 bg-muted/30 rounded-lg border border-border'>
            <p className='text-sm leading-6 space-y-1'>
              <strong>Kebi AI LLC</strong>
              <br />
              Email: <span className='text-primary'>compliance@kebi.ai</span>
              <br />
              Support: <span className='text-primary'>support@kebi.ai</span>
            </p>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>10. Relationship to Other Policies</h2>
          <p className='text-sm leading-6'>
            This Acceptable Use Policy supplements and should be read in conjunction with our Terms
            of Service and Privacy Policy. In the event of any conflict, the more restrictive
            provision shall apply.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
};
